import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate, Link } from 'react-router-dom'
import toast, { Toaster } from 'react-hot-toast' // Assuming you have this installed
import {
  Check,
  Crown,
  Star,
  ArrowRight,
  ShieldCheck,
  Globe,
  BookOpen,
  Clock,
  CreditCard,
  Zap,
  Calendar,
  Sparkles
} from 'lucide-react'

import CustomerDetailsModal from '../components/CustomerDetailsModal'
import { getPaymentPackages } from '../utils/payment'

// --- Animation Variants ---
const fadeInUp = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] }
  }
}

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.14 }
  }
}

const backendUrl = 'https://justurbane.in/backend_php/public'

const PricingPage = () => {
  const navigate = useNavigate()
  
  // --- Payment/Plan State ---
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [packages, setPackages] = useState([])
  const [loading, setLoading] = useState(true)

  // --- Magazine State ---
  const [magazines, setMagazines] = useState([])
  const [magLoading, setMagLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const planImages = {
    digital: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?q=80&w=2070&auto=format&fit=crop',
    print: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?q=80&w=1974&auto=format&fit=crop',
    combined: 'https://images.unsplash.com/photo-1493612276216-ee3925520721?q=80&w=1964&auto=format&fit=crop'
  }

  // --- Effects ---
  useEffect(() => {
    fetchPackages()
  }, [])

  useEffect(() => {
    fetchMagazines()
  }, [currentPage])

  // --- Fetch Logic: Packages ---
  const fetchPackages = async () => {
    try {
      const data = await getPaymentPackages()
      setPackages(data.packages || [])
    } catch (error) {
      console.error("Error fetching packages", error)
    } finally {
      setLoading(false)
    }
  }

  // --- Fetch Logic: Magazines ---
  const fetchMagazines = async () => {
    try {
      const token = localStorage.getItem('admin_token')
      // Note: For a public pricing page, you might need an API endpoint 
      // that doesn't require an admin_token, or ensure the token exists.
      const headers = token ? { Authorization: `Bearer ${token}` } : {}
      
      const response = await fetch(
        `${backendUrl}/api/magazines?page=${currentPage}&limit=8`, // Limit to 8 for grid balance
        { headers }
      )
      
      if (response.ok) {
        const data = await response.json()
        setMagazines(data.magazines ?? [])
        setTotalPages(data.total_pages ?? 1)
      } else {
        // Silently fail or log for public pages to avoid alerting users
        console.error('Failed to fetch magazines')
      }
    } catch (error) {
      console.error('Magazines error:', error)
      toast.error('Could not load magazine archive')
    } finally {
      setMagLoading(false)
    }
  }

  // --- Helpers ---
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const handlePlanSelect = plan => {
    setSelectedPlan(plan)
    setIsModalOpen(true)
  }

  const handlePaymentSuccess = () => {
    setIsModalOpen(false)
  }

  // --- Data Transformation ---
  const editorialPlans = packages.map(pkg => {
    let type = 'digital'
    if (pkg.id?.includes('print')) type = 'print'
    if (pkg.id?.includes('combined')) type = 'combined'

    return {
      ...pkg,
      type,
      image: planImages[type],
      category:
        type === 'combined'
          ? 'The Collection'
          : type === 'print'
          ? 'Print Edition'
          : 'Digital Access',
      subtitle:
        type === 'combined'
          ? 'Print delivered to your door with unlimited digital access.'
          : type === 'print'
          ? 'A tactile, collectible print edition for discerning readers.'
          : 'Unlimited access to the complete digital archive and exclusives.',
      features: pkg.features || []
    }
  })

  const featuredPlan = editorialPlans.find(p => p.popular) || editorialPlans[0]
  const otherPlans = editorialPlans.filter(p => p.id !== featuredPlan?.id)

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="font-serif text-xl tracking-tight">Loading the archive…</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900">
      <Toaster />

      {/* HERO */}
      <section className="bg-white border-b pt-28 pb-20 px-6">
        <motion.div
          className="max-w-6xl mx-auto text-center"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <motion.span variants={fadeInUp} className="inline-block mb-6 px-4 py-1 text-xs font-bold uppercase tracking-widest bg-blue-50 text-blue-700">
            Membership
          </motion.span>

          <motion.h1 variants={fadeInUp} className="text-5xl md:text-7xl font-serif font-bold leading-tight">
            Subscribe to <br />
            <span className="italic text-gray-500">Excellence.</span>
          </motion.h1>

          <motion.p variants={fadeInUp} className="mt-8 max-w-2xl mx-auto text-xl text-gray-500">
            A curated library of fashion, culture, ideas, and long-form journalism.
          </motion.p>
        </motion.div>
      </section>

      <main className="max-w-7xl mx-auto px-6 py-20">

        {/* FEATURED PLAN */}
        {featuredPlan && (
          <motion.section
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="mb-24"
          >
            <div className="text-center mb-10">
              <span className="text-xs font-bold uppercase tracking-widest text-gray-400">
                Most Popular Choice
              </span>
            </div>

            <div className="grid lg:grid-cols-2 bg-white border shadow-lg overflow-hidden">
              <img
                src={featuredPlan.image}
                alt={featuredPlan.name}
                className="w-full h-full object-cover"
              />

              <div className="p-10 lg:p-14 flex flex-col">
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="text-4xl font-serif font-bold">{featuredPlan.name}</h2>
                  {featuredPlan.popular && <Crown className="text-amber-500" />}
                </div>

                <p className="italic text-gray-600 mb-6">{featuredPlan.subtitle}</p>

                <div className="text-5xl font-bold mb-8">
                  ₹{featuredPlan.price}
                  <span className="text-lg text-gray-500"> / year</span>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mb-10">
                  {featuredPlan.features.map((f, i) => (
                    <div key={i} className="flex gap-3 text-sm">
                      <Check className="text-blue-600 mt-1" />
                      <span>{f}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => handlePlanSelect(featuredPlan)}
                  className="mt-auto px-10 py-4 bg-gray-900 text-white font-bold uppercase tracking-widest flex items-center justify-center gap-3 hover:bg-blue-700 transition-colors"
                >
                  Subscribe Now <ArrowRight />
                </button>
              </div>
            </div>
          </motion.section>
        )}

        {/* OTHER PLANS */}
        <section className="mb-28">
          <h3 className="text-2xl font-serif font-bold mb-10 border-b pb-4">
            More Ways to Subscribe
          </h3>

          <div className="grid md:grid-cols-2 gap-12">
            {otherPlans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: index * 0.08 }}
                className="bg-white border shadow-sm hover:shadow-xl transition"
              >
                <img src={plan.image} alt={plan.name} className="w-full h-64 object-cover" />

                <div className="p-8">
                  <h4 className="text-2xl font-bold mb-2">{plan.name}</h4>

                  <div className="text-3xl font-bold mb-4">
                    ₹{plan.price}
                    <span className="text-sm text-gray-500"> / year</span>
                  </div>

                  <p className="text-gray-500 mb-6">{plan.subtitle}</p>

                  <ul className="space-y-3 mb-8">
                    {plan.features.slice(0, 4).map((f, i) => (
                      <li key={i} className="flex gap-3 text-sm">
                        <Zap className="w-4 h-4 text-blue-600 mt-1" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handlePlanSelect(plan)}
                    className="w-full py-3 border-2 border-gray-900 text-gray-900 font-bold uppercase tracking-widest hover:bg-gray-900 hover:text-white transition-colors"
                  >
                    Select Plan
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* --- MAGAZINE ARCHIVE SECTION --- */}
        <section className="mb-28">
           <div className="flex items-end justify-between border-b pb-4 mb-10">
              <h3 className="text-2xl font-serif font-bold">
                The Digital Archive
              </h3>
              <div className="hidden sm:flex items-center gap-2 text-amber-600 text-sm font-medium">
                 <Sparkles className="w-4 h-4" />
                 <span>Exclusive Member Access</span>
              </div>
           </div>

           {magLoading ? (
              <div className="flex justify-center py-20">
                 <div className="w-8 h-8 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin"></div>
              </div>
           ) : (magazines.length || 0) === 0 ? (
              <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
                 <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                 <h3 className="text-lg font-medium text-gray-900 mb-2">Archive temporarily unavailable</h3>
                 <p className="text-gray-500">Please check back later for our digital collection.</p>
              </div>
           ) : (
              <>
                 <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {magazines.map((magazine) => (
                       <Link
                          key={magazine.id}
                          to={`/magazine/${magazine.id}`}
                          className="group bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-xl transition-all duration-300"
                       >
                          {/* Magazine Cover */}
                          <div className="aspect-[3/4] bg-gray-100 flex items-center justify-center relative overflow-hidden">
                             {magazine.cover_image ? (
                                <img
                                   src={magazine.cover_image}
                                   alt={magazine.title}
                                   className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                             ) : (
                                <div className="flex flex-col items-center justify-center h-full">
                                   <BookOpen className="w-12 h-12 text-gray-300 mb-2" />
                                   <p className="text-xs text-gray-400">Preview</p>
                                </div>
                             )}

                             {magazine.is_featured && (
                                <div className="absolute top-2 right-2 bg-amber-500 text-white px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest flex items-center shadow-sm">
                                   <Star className="w-3 h-3 mr-1" /> Featured
                                </div>
                             )}
                          </div>

                          {/* Magazine Info */}
                          <div className="p-4">
                             <div className="flex items-center text-xs text-blue-600 font-bold uppercase tracking-wider mb-2">
                                <Calendar className="w-3 h-3 mr-1" />
                                <span>{magazine.month} {magazine.year}</span>
                             </div>
                             
                             <h3 className="font-serif font-bold text-lg leading-tight mb-2 group-hover:text-blue-700 transition-colors line-clamp-1">
                                {magazine.title}
                             </h3>
                             
                             <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                                {magazine.description}
                             </p>

                             {magazine.file_size && (
                                <div className="pt-3 border-t text-xs text-gray-400 flex justify-between">
                                   <span>PDF Edition</span>
                                   <span>{formatFileSize(magazine.file_size)}</span>
                                </div>
                             )}
                          </div>
                       </Link>
                    ))}
                 </div>

                 {/* Pagination */}
                 {totalPages > 1 && (
                    <div className="mt-10 flex justify-center">
                       <nav className="flex items-center space-x-2">
                          <button
                             onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                             disabled={currentPage === 1}
                             className="px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                             Previous
                          </button>

                          <span className="px-4 py-2 text-sm text-gray-500 font-medium">
                             Page {currentPage} of {totalPages}
                          </span>

                          <button
                             onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                             disabled={currentPage === totalPages}
                             className="px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                             Next
                          </button>
                       </nav>
                    </div>
                 )}
              </>
           )}
        </section>

        {/* TRUST INDICATORS */}
        <section className="grid md:grid-cols-4 gap-10 mt-28 text-center border-t pt-16">
          <div>
            <BookOpen className="mx-auto mb-3 text-gray-400" />
            <p className="font-bold">Complete Archive</p>
            <p className="text-sm text-gray-500">All issues since 2018</p>
          </div>
          <div>
            <ShieldCheck className="mx-auto mb-3 text-gray-400" />
            <p className="font-bold">Secure Payments</p>
            <p className="text-sm text-gray-500">Razorpay protected</p>
          </div>
          <div>
            <Clock className="mx-auto mb-3 text-gray-400" />
            <p className="font-bold">Cancel Anytime</p>
            <p className="text-sm text-gray-500">No lock-in</p>
          </div>
          <div>
            <CreditCard className="mx-auto mb-3 text-gray-400" />
            <p className="font-bold">Instant Access</p>
            <p className="text-sm text-gray-500">Read immediately</p>
          </div>
        </section>

      </main>

      <footer className="bg-white border-t py-16 text-center">
        <h2 className="font-serif text-2xl font-bold">Just Urbane</h2>
        <p className="text-xs uppercase tracking-widest text-gray-400 mt-2">Est. 2018</p>

        <div className="flex justify-center gap-10 mt-8 text-sm">
          <Link to="/contact" className="hover:text-blue-600">Contact</Link>
          <Link to="/corporate" className="hover:text-blue-600">Corporate</Link>
          <Link to="/gift" className="hover:text-blue-600">Gift</Link>
        </div>

        <p className="text-xs text-gray-400 mt-8">
          Secure payments • Cancel anytime
        </p>
      </footer>

      <CustomerDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedPlan={selectedPlan}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  )
}

export default PricingPage